import React, { useState } from 'react';
import { useInviteService } from '../services/invite';
import { useUserService } from '../services/user';
import '../support/styles.css';
import './styles.css';

export interface InviteFormProps {
    id: string;
    onSubmissionSuccess: () => void;
}

interface FieldState {
    value: string;
    error: string;
    loading?: boolean;
}

const emptyField: FieldState = { value: '', error: '' };

const normalizePhoneNumber = (phone: string) => {
    const sanitized = phone.replace(/\D/g, '');
    return sanitized.length > 10 ? `+${sanitized}` : `+1${sanitized}`;
};

const validatePassword = (password: string) => {
    if (!password) {
        return 'Password is required.';
    }
    if (!/[A-Z]/.test(password)) {
        return 'Password must contain at least one uppercase letter.';
    }
    if (!/[a-z]/.test(password)) {
        return 'Password must contain at least one lowercase letter.';
    }
    if (!/[0-9]/.test(password)) {
        return 'Password must contain at least one number.';
    }
    if (!/[!@#$%^&*]/.test(password)) {
        return 'Password must contain at least one special character.';
    }
    return '';
};

export const InviteForm = ({ id, onSubmissionSuccess }: InviteFormProps) => {
    const [name, setName] = useState<FieldState>(emptyField);
    const [email, setEmail] = useState<FieldState>(emptyField);
    const [phoneNumber, setPhoneNumber] = useState<FieldState>(emptyField);
    const [username, setUsername] = useState<FieldState>(emptyField);
    const [password, setPassword] = useState<FieldState>(emptyField);
    const [confirmPassword, setConfirmPassword] = useState<FieldState>(emptyField);
    const [status, setStatus] = useState<string | undefined>();
    const [error, setError] = useState<string | null>(null);
    const inviteService = useInviteService();
    const userService = useUserService();
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const nameError = name.value ? '' : 'Name is required.';
        const emailError = email.value ? '' : 'Email is required.';
        const phoneNumberError = phoneNumber.value ? '' : 'Phone number is required.';
        const usernameError = username.value ? '' : 'Username is required.';
        const passwordError = validatePassword(password.value);
        const confirmPasswordError = !confirmPassword.value
            ? 'Please confirm your password.'
            : password.value && password.value !== confirmPassword.value
                ? 'Passwords do not match.'
                : '';

        setName((prev) => ({ ...prev, error: nameError }));
        setEmail((prev) => ({ ...prev, error: emailError }));
        setPhoneNumber((prev) => ({ ...prev, error: phoneNumberError }));
        setUsername((prev) => ({ ...prev, error: usernameError }));
        setPassword((prev) => ({ ...prev, error: passwordError }));
        setConfirmPassword((prev) => ({ ...prev, error: confirmPasswordError }));

        const hasErrors = [
            nameError,
            emailError,
            phoneNumberError,
            usernameError,
            passwordError,
            confirmPasswordError,
        ].some(Boolean);

        if (hasErrors) {
            setError('Please fix the errors below before submitting.');
        } else {
            setError(null);
            setStatus('submitting');
            inviteService.acceptInvite({
                inviteId: id,
                name: name.value,
                email: email.value,
                phone: normalizePhoneNumber(phoneNumber.value),
                username: username.value,
                password: password.value
            }).then(onSubmissionSuccess).catch((error) => {
                if ('message' in error && typeof error.message === 'string') {
                    setError(
                        error.message === 'Invite not found'
                            ? 'The invite link has either expired or the information provided does not match our records.'
                            : error.message
                    );
                }
                setStatus('error');
            });
        }
    };

    const inputClassName = (field: FieldState) =>
        `support-input${field.error ? ' support-input--error' : ''}`;

    const onEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !email.error) {
            setEmail(prev => ({ ...prev, loading: true }));
            userService.getUserAvailable({ email: value }).then(({ available }) => {
                setEmail(prev => ({ ...prev, error: !available ? 'Email is already taken.' : '', loading: false }));
            });
        }
    };

    const onPhoneNumberBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const normalizedValue = normalizePhoneNumber(value);
        if (normalizedValue.length === 12) {
            setPhoneNumber({ value, error: '', loading: true });
            userService.getUserAvailable({ phone: `"${normalizedValue}"` }).then(({ available }) => {
                setPhoneNumber(prev => ({ ...prev, error: !available ? 'Phone is already taken.' : '', loading: false }));
            });
        }
    };

    const onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const error = value.length > 20 ? 'Username must be 20 characters or less.' : '';
        setUsername({ value, error });
    };

    const onUsernameBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value && !username.error) {
            setUsername(prev => ({ ...prev, loading: true }));
            userService.getUserAvailable({ username: value }).then(({ available }) => {
                setUsername(prev => ({ ...prev, error: !available ? 'Username is already taken.' : '', loading: false }));
            });
        }
    };

    const onConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const error = password.value && password.value !== value ? 'Passwords do not match.' : '';
        setConfirmPassword({ value, error });
    };

    const availabilityCheckInProgress = email.loading || phoneNumber.loading || username.loading;
    const criticalFieldsHaveError = !!email.error || !!phoneNumber.error || !!username.error;

    return (
        <form className="support-form" onSubmit={handleSubmit}>
            <div className="support-field">
                <label htmlFor="invite-name">Name</label>
                <input
                    id="invite-name"
                    className={inputClassName(name)}
                    type="text"
                    value={name.value}
                    onChange={(e) => setName({ value: e.target.value, error: '' })}
                    disabled={status === 'submitting'}
                />
                {name.error && <p className="support-field-error">{name.error}</p>}
            </div>

            <div className="support-field">
                <label htmlFor="invite-email">Email</label>
                <input
                    id="invite-email"
                    className={inputClassName(email)}
                    type="email"
                    value={email.value}
                    onChange={(e) => setEmail({ value: e.target.value, error: '' })}
                    onBlur={onEmailBlur}
                    disabled={status === 'submitting'}
                />
                {email.error && <p className="support-field-error">{email.error}</p>}
            </div>

            <div className="support-field">
                <label htmlFor="invite-phone">Phone Number</label>
                <input
                    id="invite-phone"
                    className={inputClassName(phoneNumber)}
                    type="tel"
                    value={phoneNumber.value}
                    onChange={(e) => setPhoneNumber({ value: e.target.value, error: '' })}
                    onBlur={onPhoneNumberBlur}
                    disabled={status === 'submitting'}
                />
                {phoneNumber.error && <p className="support-field-error">{phoneNumber.error}</p>}
            </div>

            <div className="support-field">
                <label htmlFor="invite-username">Username</label>
                <input
                    id="invite-username"
                    className={inputClassName(username)}
                    type="text"
                    value={username.value}
                    onChange={onUsernameChange}
                    onBlur={onUsernameBlur}
                    disabled={status === 'submitting'}
                />
                {username.error && <p className="support-field-error">{username.error}</p>}
            </div>

            <div className="support-field">
                <label htmlFor="invite-password">Password</label>
                <input
                    id="invite-password"
                    className={inputClassName(password)}
                    type="password"
                    value={password.value}
                    onChange={(e) => setPassword({ value: e.target.value, error: '' })}
                    disabled={status === 'submitting'}
                />
                {password.error && <p className="support-field-error">{password.error}</p>}
            </div>

            <div className="support-field">
                <label htmlFor="invite-confirm-password">Confirm Password</label>
                <input
                    id="invite-confirm-password"
                    className={inputClassName(confirmPassword)}
                    type="password"
                    value={confirmPassword.value}
                    onChange={onConfirmPasswordChange}
                    disabled={status === 'submitting'}
                />
                {confirmPassword.error && <p className="support-field-error">{confirmPassword.error}</p>}
            </div>

            {error && <p className="survey-error">{error}</p>}

            <div className="support-form-footer">
                <button type="submit" className="survey-submit-btn" disabled={status === 'submitting' || availabilityCheckInProgress || criticalFieldsHaveError}>
                    {status === 'submitting' ? <span className="invite-submit-spinner"/> : 'Submit'}
                </button>
            </div>
        </form>
    );
};

export default InviteForm;
